"use client";

import React from "react";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";

import Chip from "@/components/global/ui/Chip";

import {PageVslLongSchema} from "@/sanity/pages/PageVslLongTemplate";
import DecorationLine from "../global/ui/DecorationLine";

export default function StepsSection({
  data,
  className,
}: {
  data: PageVslLongSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative overflow-clip",
        className,
      )}
    >
      {/* side lines flanking the content */}
      <DecorationLine
      className="top-0"
        orientation="vertical"
        position="left"
        style="dark"
      />
      <DecorationLine
      className="top-0"
        orientation="vertical"
        position="right"
        style="dark"
      />
      <div className="w-[min(180rem,100%)] mx-auto px-[2rem] sm:px-[3rem] lg:px-[0rem] py-[9rem] lg:py-[14rem]">
        {/* header */}
        <div className="flex flex-col items-center text-center gap-[2.4rem]">
          {data?.stepsChip?.text && (
            <Chip
              className="mb-0 [&_p]:uppercase"
              image={data?.stepsChip?.icon}
              text={data?.stepsChip?.text}
            />
          )}

          {data?.stepsTitle && (
            <div className="richtext richtext-center w-full prose-headings:font-ivy prose-headings:font-normal prose-headings:text-black prose-headings:text-[4rem] sm:prose-headings:text-[4.8rem] lg:prose-headings:text-[5.6rem] prose-headings:leading-[1.1] prose-headings:text-balance prose-em:font-ivy">
              <PortableText
                value={data.stepsTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* steps */}
        {data?.stepsItems && data.stepsItems.length > 0 && (
          <div className="mt-[4.8rem] flex flex-col lg:flex-row items-stretch gap-[.8rem]">
            {data.stepsItems.map((step, index) => (
              <div
                key={index}
                className="flex-1 flex items-stretch overflow-clip"
              >
                {/* number tag + accent line */}
                <div className="flex items-start shrink-0">
                  <div className="flex items-start justify-center h-[6rem] w-[3.25rem]">
                    <div className="bg-[#D9F9B9] flex items-center justify-center w-full h-[6rem] [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))]">
                      <span className="font-geistmono font-medium text-[2rem] leading-none text-black">
                        {index + 1}
                      </span>
                    </div>
                  </div>
                  <div className="bg-[#D9F9B9] w-[.2rem] self-stretch" />
                </div>

                {/* content */}
                <div className="bg-white flex-1 flex flex-col justify-center px-[3rem] py-[1.5rem]">
                  {step?.title && (
                    <p className="mb-0 font-geist text-black text-[2.25rem] leading-[1.5]">
                      {step.title}
                    </p>
                  )}
                  {step?.text && (
                    <p className="mb-0 font-geist text-black/60 text-[2.25rem] leading-[1.5] whitespace-pre-line">
                      {step.text}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
